import { useState, useEffect } from 'react';
import { Table, Button, Upload, message, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import userLotteryApi from 'api/userLottery';
import { useTranslation } from 'react-i18next';
import styles from '../index.module.scss';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const { t } = useTranslation();

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userLotteryApi.getAll({});
            const userList = response.data || [];
            setUsers(userList);
            setPagination(prev => ({
                ...prev,
                total: userList.length,
            }));
        } catch (error) {
            message.error(t('hasError'));
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async (file) => {
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

                    // Đọc dạng array thay vì object để không phụ thuộc vào header
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

                    // Bỏ qua dòng đầu tiên (header) nếu có
                    const dataRows = jsonData.slice(1);

                    // Transform data: [code, fullName, area, isMarker]
                    const formattedUsers = dataRows
                        .filter(row => row && row.length > 0) // Bỏ qua dòng trống
                        .map(row => ({
                            code: row[0],
                            fullName: String(row[1] || '').trim(),
                            area: String(row[2] || '1').trim(),
                            isMarker: !!row[3],
                        }));

                    // Validate data
                    const invalidUsers = formattedUsers.filter(
                        user => !user.code || !user.fullName || !user.area
                    );

                    if (invalidUsers.length > 0) {
                        message.error(t('invalidDataInExcel'));
                        return;
                    }

                    if (formattedUsers.length === 0) {
                        message.error(t('noDataInExcel'));
                        return;
                    }

                    // Call API to create users
                    setLoading(true);
                    await userLotteryApi.createMany({ users: formattedUsers });
                    message.success(t('importSuccessfully'));
                    fetchUsers();
                } catch (error) {
                    message.error(t('importFailed'));
                    console.error('Error parsing Excel:', error);
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (error) {
            message.error(t('hasError'));
            console.error('Error importing users:', error);
        }
        return false; // Prevent default upload behavior
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const columns = [
        {
            title: t('No.'),
            key: 'index',
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: t('code'),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t('fullName'),
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: t('area'),
            dataIndex: 'area',
            key: 'area',
            render: (_) => <Tag color="default">{_}</Tag>,
        },
        {
            title: t('isMarker'),
            dataIndex: 'isMarker',
            key: 'isMarker',
            render: (isMarker) => (
                <Tag color={isMarker ? 'green' : 'default'}>
                    {isMarker ? t('yes') : t('no')}
                </Tag>
            ),
        },
    ];

    return (
        <div className={styles.userList}>
            <div className={styles.header}>
                <h3>{t('listEmployee')}</h3>
                <Upload
                    accept=".xlsx,.xls"
                    beforeUpload={handleImport}
                    showUploadList={false}
                >
                    <Button icon={<UploadOutlined />} loading={loading}>
                        {t('import')}
                    </Button>
                </Upload>
            </div>

            <Table
                dataSource={users}
                columns={columns}
                rowKey="_id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default UserList;
